/**
 * UniversalArchiveSection — Composant réutilisable pour afficher les items archivés
 * de tous les types métier (Batch #14.200).
 *
 * Workflow 3 paliers : Actif → Archivé → Corbeille → Supprimé.
 *
 * Props :
 *   - mode: "archive" | "trash"
 *   - onRefresh?: callback après une action (pour recharger les compteurs du parent)
 *
 * Affiche un sélecteur de type (12 types) + table des items + boutons d'action contextuel.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Loader2, RotateCcw, Trash2, AlertTriangle, X, ShieldAlert, FileText, Wrench, UserPlus, ScrollText, FileSignature, MessageSquare, Send, Bell, BellRing, Smartphone, Activity, Search } from "lucide-react";

// Icône + couleur par type (utilise les mêmes clés que ITEM_TYPES backend)
const TYPE_META = {
  invoice:             { label: "Factures",            icon: FileText,       color: "text-blue-600 bg-blue-50",       getTitle: (it) => `#${it.invoice_number || "?"} — ${it.client_name || "?"} (${it.total ?? 0} $)` },
  work_order:          { label: "Travaux",             icon: Wrench,         color: "text-amber-600 bg-amber-50",     getTitle: (it) => it.detail || "Travaux sans description" },
  lead:                { label: "Prospects",           icon: UserPlus,       color: "text-purple-600 bg-purple-50",   getTitle: (it) => `${it.name || "—"} (${it.email || "—"})` },
  rl31_request:        { label: "Demandes RL-31",      icon: ScrollText,     color: "text-emerald-600 bg-emerald-50", getTitle: (it) => `RL-31 ${it.year || "?"}` },
  rent_increase:       { label: "Avis renouvellement", icon: ScrollText,     color: "text-rose-600 bg-rose-50",       getTitle: (it) => `Avis ${it.tenant_name || "?"} (${it.current_rent ?? "?"} → ${it.proposed_rent ?? "?"} $)` },
  bail_signature:      { label: "Signatures bail",     icon: FileSignature,  color: "text-cyan-600 bg-cyan-50",       getTitle: (it) => `Bail ${it.tenant_name || it.client_id || "?"}` },
  question:            { label: "Questions",           icon: MessageSquare,  color: "text-indigo-600 bg-indigo-50",   getTitle: (it) => it.subject || it.title || "Question" },
  communication:       { label: "Communications",     icon: Send,           color: "text-pink-600 bg-pink-50",       getTitle: (it) => it.title || it.subject || "(sans sujet)" },
  admin_notification:  { label: "Notif. cabinet",      icon: Bell,           color: "text-sky-600 bg-sky-50",         getTitle: (it) => it.title || it.message?.slice(0, 60) || "Notification" },
  client_notification: { label: "Notif. client",       icon: BellRing,       color: "text-teal-600 bg-teal-50",       getTitle: (it) => it.title || it.message?.slice(0, 60) || "Notification" },
  sms_log:             { label: "SMS envoyés",         icon: Smartphone,     color: "text-orange-600 bg-orange-50",   getTitle: (it) => `${it.to_raw || it.to || "?"} → ${it.text_preview || "..."}` },
  notif_log:           { label: "Tracking",            icon: Activity,       color: "text-violet-600 bg-violet-50",   getTitle: (it) => `${it.related_type || ""} → ${it.recipient_email || it.recipient_phone || "?"}` },
};

export default function UniversalArchiveSection({ mode, onRefresh }) {
  const isTrash = mode === "trash";
  const [counts, setCounts] = useState({});
  const [activeType, setActiveType] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmInput, setConfirmInput] = useState("");

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(isTrash ? "/admin/trash/all" : "/admin/archive/all");
      setCounts(r.data?.counts || {});
      // Sélectionner le premier type non-vide par défaut
      const firstNonEmpty = Object.entries(r.data?.counts || {}).find(([, c]) => c > 0)?.[0];
      const initial = activeType && (r.data?.counts?.[activeType] != null) ? activeType : firstNonEmpty || "invoice";
      setActiveType(initial);
      setItems(r.data?.items_by_type?.[initial] || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTrash]);

  const switchType = useCallback(async (t) => {
    setActiveType(t);
    setLoading(true);
    try {
      const r = await api.get(`${isTrash ? "/admin/trash" : "/admin/archive"}/${t}`);
      setItems(r.data?.items_by_type?.[t] || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  }, [isTrash]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const s = search.toLowerCase();
    return items.filter((it) => JSON.stringify(it).toLowerCase().includes(s));
  }, [items, search]);

  const refreshAfterAction = async () => {
    await loadAll();
    onRefresh?.();
  };

  // Actions
  const restoreToActive = async (it) => {
    if (!window.confirm(`Réactiver cet élément ?\n\nIl reviendra dans la liste active.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/archive/${activeType}/${it.id}/restore`);
      toast.success("Élément réactivé");
      refreshAfterAction();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const sendToTrash = async (it) => {
    if (!window.confirm(`Envoyer à la corbeille ?\n\nIl sera marqué pour suppression définitive. Encore récupérable depuis la Corbeille.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/trash/${activeType}/${it.id}`);
      toast.success("Envoyé à la corbeille");
      refreshAfterAction();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const restoreFromTrash = async (it) => {
    if (!window.confirm(`Sortir de la corbeille ?\n\nL'élément retourne dans l'archive.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/trash/${activeType}/${it.id}/restore`);
      toast.success("Sorti de la corbeille");
      refreshAfterAction();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const confirmHardDelete = async () => {
    if (!deleteTarget) return;
    if (confirmInput !== "SUPPRIMER") {
      toast.error('Vous devez taper exactement "SUPPRIMER"');
      return;
    }
    setBusy(true);
    try {
      await api.delete(`/admin/trash/${activeType}/${deleteTarget.id}?confirm=SUPPRIMER`);
      toast.success("Supprimé définitivement");
      setDeleteTarget(null);
      setConfirmInput("");
      refreshAfterAction();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  // Render
  const typesList = Object.entries(TYPE_META).map(([k, v]) => ({ key: k, ...v, count: counts[k] || 0 }));

  return (
    <div data-testid={`universal-${mode}-section`}>
      {/* Type chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        {typesList.map((t) => {
          const Icon = t.icon;
          const isActive = activeType === t.key;
          return (
            <button
              key={t.key}
              onClick={() => switchType(t.key)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition ${
                isActive
                  ? "border-slate-800 bg-slate-800 text-white"
                  : t.count > 0
                    ? `${t.color} border-current/20 hover:opacity-80`
                    : "border-slate-200 text-slate-400 bg-white hover:bg-slate-50"
              }`}
              data-testid={`${mode}-type-chip-${t.key}`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{t.label}</span>
              <span className={`ml-0.5 inline-flex items-center justify-center w-4 h-4 text-[10px] rounded-full ${
                isActive ? "bg-white text-slate-800" : "bg-slate-200 text-slate-700"
              }`}>{t.count}</span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative max-w-sm mb-3">
        <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher dans cette section..."
          className="pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm w-full"
          data-testid={`${mode}-universal-search`}
        />
      </div>

      {/* Items list */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="inline w-7 h-7 animate-spin text-slate-300" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-sm text-slate-400" data-testid={`${mode}-universal-empty`}>
            {items.length === 0
              ? (isTrash ? "Aucun élément dans la corbeille pour ce type." : "Aucun élément archivé pour ce type.")
              : "Aucun résultat pour cette recherche."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Élément</th>
                <th className="text-left px-4 py-3">{isTrash ? "Mis en corbeille" : "Archivé le"}</th>
                <th className="text-left px-4 py-3">Par</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => {
                const meta = TYPE_META[activeType] || {};
                const Icon = meta.icon;
                const title = meta.getTitle ? meta.getTitle(it) : (it.title || it.id);
                const at = isTrash ? (it.trashed_at || it.archived_at) : it.archived_at;
                const by = isTrash ? (it.trashed_by || it.archived_by) : it.archived_by;
                return (
                  <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`${mode}-universal-row-${it.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800 inline-flex items-center gap-2">
                        {Icon && <Icon className="w-3.5 h-3.5 text-slate-500" />}
                        <span>{title}</span>
                      </div>
                      <div className="text-[10px] text-slate-400 font-mono mt-0.5">{it.id?.slice(0, 8)}…</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {at ? new Date(at).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{by || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2">
                        {isTrash ? (
                          <>
                            <button
                              onClick={() => restoreFromTrash(it)}
                              disabled={busy}
                              className="text-xs px-2.5 py-1 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 inline-flex items-center gap-1 disabled:opacity-50"
                              data-testid={`${mode}-universal-restore-${it.id}`}
                            >
                              <RotateCcw className="w-3 h-3" /> Restaurer
                            </button>
                            <button
                              onClick={() => { setDeleteTarget(it); setConfirmInput(""); }}
                              disabled={busy}
                              className="text-xs px-2.5 py-1 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 inline-flex items-center gap-1 disabled:opacity-50"
                              data-testid={`${mode}-universal-delete-${it.id}`}
                            >
                              <Trash2 className="w-3 h-3" /> Supprimer
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => restoreToActive(it)}
                              disabled={busy}
                              className="text-xs px-2.5 py-1 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 inline-flex items-center gap-1 disabled:opacity-50"
                              data-testid={`${mode}-universal-reactivate-${it.id}`}
                            >
                              <RotateCcw className="w-3 h-3" /> Réactiver
                            </button>
                            <button
                              onClick={() => sendToTrash(it)}
                              disabled={busy}
                              className="text-xs px-2.5 py-1 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 inline-flex items-center gap-1 disabled:opacity-50"
                              data-testid={`${mode}-universal-to-trash-${it.id}`}
                              title="Envoyer à la corbeille"
                            >
                              <Trash2 className="w-3 h-3" /> Corbeille →
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid={`${mode}-universal-confirm-modal`}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-rose-700" />
                </div>
                <h3 className="font-bold text-slate-800">Suppression définitive</h3>
              </div>
              <button onClick={() => { setDeleteTarget(null); setConfirmInput(""); }} className="p-1 rounded hover:bg-slate-100">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <p className="text-sm text-slate-600 mb-3">
              Cet élément sera <b>définitivement supprimé de la base de données</b> :
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700 font-mono mb-4 break-all">
              {TYPE_META[activeType]?.getTitle ? TYPE_META[activeType].getTitle(deleteTarget) : deleteTarget.id}
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-900">
              <ShieldAlert className="w-4 h-4 inline mr-1" />
              Cette action est <b>irréversible</b>. Aucun retour arrière possible.
            </div>
            <label className="block mb-4">
              <span className="text-xs font-medium text-slate-700">
                Pour confirmer, tapez <code className="bg-slate-100 px-1.5 py-0.5 rounded text-rose-700 font-mono">SUPPRIMER</code> ci-dessous :
              </span>
              <input
                type="text"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                className="mt-1 w-full px-3 py-2 border-2 border-rose-200 rounded-lg text-sm focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none font-mono"
                placeholder="SUPPRIMER"
                autoFocus
                data-testid={`${mode}-universal-confirm-input`}
              />
            </label>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setDeleteTarget(null); setConfirmInput(""); }}
                className="px-4 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50"
                data-testid={`${mode}-universal-confirm-cancel`}
              >
                Annuler
              </button>
              <button
                onClick={confirmHardDelete}
                disabled={busy || confirmInput !== "SUPPRIMER"}
                className="px-4 py-2 text-sm rounded-lg bg-rose-600 text-white hover:bg-rose-700 inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`${mode}-universal-confirm-delete`}
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                <Trash2 className="w-4 h-4" />
                Supprimer définitivement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
